import { MeasureHelper } from '@core/helpers/measure.helper';
import { fromCore, fromRouter } from '@core/store';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { Panel } from '@shared/gui/models/equipment.model';

import { State, settingsFeatureKey } from './settings.reducer';

const selectSettingsState = createFeatureSelector<State>(settingsFeatureKey);

// Raw selectors

export const getPistonTrackPanelStep = createSelector(
  selectSettingsState,
  (state: State) => state.pistonTrackPanelStep
);

export const getPistonTrack = createSelector(selectSettingsState, (state: State) => state.pistonTrack);

export const getPistonTrackErrors = createSelector(selectSettingsState, (state: State) => state.pistonTrackErrors);

export const getPistonTrackExtremities = createSelector(
  selectSettingsState,
  (state: State) => state.pistonTrackExtremities
);

export const getTopology = createSelector(selectSettingsState, (state: State) => state.topology);

// Calculated selectors

export const getSelectedEquipment = createSelector(
  fromCore.getEquipments,
  fromRouter.selectRouteParams,
  (equipments, routeParams) =>
    equipments.find(equipment => equipment.id.toUpperCase() === routeParams.id?.toUpperCase())
);

export const getSelectedEquipmentName = createSelector(
  getSelectedEquipment,
  equipment => equipment && (equipment.properties.find(p => p.name === 'name').value as string)
);

export const getSelectedEquipmentGasxProperties = createSelector(getSelectedEquipment, equipment =>
  equipment ? equipment.properties.filter(p => p.panel === Panel.GASX) : []
);

export const getSelectedEquipmentMeasuredProperties = createSelector(getSelectedEquipment, equipment =>
  equipment
    ? equipment.properties
        .filter(p => p.panel === Panel.MEASURED)
        .map(p => ({ ...MeasureHelper.generateManualProperty(p), name: p.name }))
    : []
);

export const getEquipmentAvailable = createSelector(fromCore.getEquipments, equipments =>
  equipments.filter(
    equipment => equipment.id !== 'AuxiliaryPipe' && equipment.id !== 'DigitalMeter' && equipment.id !== 'DashboardLink'
  )
);

export const getEquipmentReferential = createSelector(
  fromCore.getReferentials,
  getSelectedEquipment,
  (referentials, equipment) => {
    const lists = [...new Set(equipment?.properties?.filter(prop => prop.listId).map(x => x.listId))];
    const refId = lists.find(ref => ref.toLowerCase().includes(equipment.id.toLowerCase()));
    return referentials.find(x => x.id === refId);
  }
);

export const getSelectedColorationContext = createSelector(
  fromCore.getColorationContexts,
  fromRouter.selectRouteParams,
  (colorationContexts, routeParams) =>
    colorationContexts.find(colorationContext => colorationContext.id === routeParams.id)
);

export const getSelectedColorationGasBlock = createSelector(
  fromCore.getColorationGasBlocks,
  fromRouter.selectRouteParams,
  (colorationGasBlocks, routeParams) =>
    colorationGasBlocks.find(colorationGasBlock => colorationGasBlock.id === routeParams.id)
);

export const getSelectedPistonType = createSelector(
  fromCore.getPistonTypes,
  fromRouter.selectRouteParams,
  (pistonTypes, routeParams) => pistonTypes.find(pistonType => pistonType.id === routeParams.id)
);

export const getSelectedPistonTrack = createSelector(
  fromCore.getPistonTracks,
  fromRouter.selectRouteParams,
  (pistonTracks, routeParams) => pistonTracks.find(pistonTrack => pistonTrack.id === routeParams.id)
);

export const isDiagramEnable = createSelector(
  getPistonTrackPanelStep,
  pistonTrackPanelStep => pistonTrackPanelStep === 1
);
